import { Flex, Input, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { capitalizeName, EMAIL_REGEX, hasFullName, useAppToast } from '../../../helpers'
import { TestimonialVideoService } from '../../../services/TestimonialVideo/TestimonialVideoService'
import {
  ITestimonialVideoAddress,
  ITestimonialVideoCourseOption,
  TestimonialVideoModality
} from '../../../services/TestimonialVideo/types'
import AddressFields from './AddressFields'
import { EMPTY_ADDRESS, MODALITY_OPTIONS, trimAddress, validateAddress, validationErrorsOf } from './consts'
import CoursesLoadError from './CoursesLoadError'

interface ITestimonialVideoEditModalProps {
  id: number | null
  onClose: (updated?: boolean) => void
}

const emptyForm = { name: '', email: '', modality: '' as '' | TestimonialVideoModality, courseId: '' }

/** Edits the student, address and course data. The video and the consent proof are not editable. */
export default function TestimonialVideoEditModal({ id, onClose }: ITestimonialVideoEditModalProps) {
  const service = new TestimonialVideoService()
  const toast = useAppToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ ...emptyForm })
  const [address, setAddress] = useState<ITestimonialVideoAddress>({ ...EMPTY_ADDRESS })
  // The submission's own course, kept selectable even if it left the catalog since.
  const [originalCourse, setOriginalCourse] = useState<{
    modality: TestimonialVideoModality
    course: ITestimonialVideoCourseOption
  } | null>(null)
  const [courses, setCourses] = useState<ITestimonialVideoCourseOption[]>([])
  const [loadingCourses, setLoadingCourses] = useState(false)
  const [coursesLoadFailed, setCoursesLoadFailed] = useState(false)
  // Bumped by "Tentar novamente" to run the course effect again.
  const [coursesAttempt, setCoursesAttempt] = useState(0)
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})

  const error = (key: string) => formErrors[key]?.[0]

  // Same render-time adjustment as SalesReceiptEditModal: the skeleton shows on the very
  // first frame after opening, instead of the previous record.
  const [openedId, setOpenedId] = useState(id)
  if (id !== openedId) {
    setOpenedId(id)
    if (id !== null) setLoading(true)
  }

  useEffect(() => {
    if (id === null) return

    const fetch = async () => {
      setFormErrors({})
      try {
        const submission = await service.get(id)
        setForm({
          name: submission.name,
          email: submission.email,
          modality: submission.modality,
          courseId: String(submission.course_id)
        })
        setAddress({
          zip_code: submission.zip_code,
          street: submission.street,
          number: submission.number,
          complement: submission.complement ?? '',
          neighborhood: submission.neighborhood,
          city: submission.city,
          state: submission.state
        })
        setOriginalCourse({
          modality: submission.modality,
          course: { id: submission.course_id, name: submission.course_name ?? `Curso #${submission.course_id}` }
        })
      } catch (e) {
        toast.error('Erro ao carregar o vídeo de depoimento', e)
        onClose()
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [id])

  useEffect(() => {
    if (id === null || !form.modality) return

    const modality = form.modality
    // Switching modality quickly must not let a late response overwrite the current list.
    let cancelled = false
    setLoadingCourses(true)
    setCoursesLoadFailed(false)
    service
      .getFormCourses(modality)
      .then((list) => {
        if (cancelled) return
        const own = originalCourse?.modality === modality ? originalCourse.course : null
        const merged = own && !list.some((course) => course.id === own.id) ? [...list, own] : list
        setCourses(merged.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')))
      })
      .catch((e) => {
        if (cancelled) return
        // Never leave the previous modality's courses selectable.
        setCourses([])
        setCoursesLoadFailed(true)
        toast.error('Erro ao carregar os cursos', e)
      })
      .finally(() => !cancelled && setLoadingCourses(false))

    return () => {
      cancelled = true
    }
  }, [id, form.modality, originalCourse, coursesAttempt])

  const validate = (): Record<string, string[]> => {
    const errors: Record<string, string[]> = {}

    if (!hasFullName(form.name)) errors.name = ['Informe o nome e sobrenome.']
    if (!EMAIL_REGEX.test(form.email.trim())) errors.email = ['Informe um e-mail válido.']
    Object.assign(errors, validateAddress(address))
    if (!form.modality) errors.modality = ['A modalidade é obrigatória.']
    if (!form.courseId) errors.course_id = ['O curso é obrigatório.']

    return errors
  }

  const handleSave = async () => {
    if (id === null) return

    const clientErrors = validate()
    setFormErrors(clientErrors)
    if (Object.keys(clientErrors).length > 0) {
      toast.invalidForm()
      return
    }

    setSaving(true)
    try {
      await service.update(id, {
        name: form.name.trim(),
        email: form.email.trim(),
        ...trimAddress(address),
        modality: form.modality as TestimonialVideoModality,
        course_id: Number(form.courseId)
      })
      toast.success('Vídeo de depoimento atualizado')
      onClose(true)
    } catch (err) {
      const fieldErrors = validationErrorsOf(err)
      if (fieldErrors) {
        setFormErrors(fieldErrors)
        toast.invalidForm()
      } else {
        toast.error('Erro ao salvar o vídeo de depoimento', err)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={id !== null}
      onClose={() => onClose()}
      title="Editar Vídeo de Depoimento"
      size="wide"
      onSubmit={handleSave}
      isLoading={saving}
      isConfirmDisabled={loading}
    >
      {loading ? (
        <LoadingBlock />
      ) : (
        <>
          <Text variant="caption" mb={4}>
            O vídeo e o aceite do termo não podem ser alterados.
          </Text>

          <FormField label="Nome completo" error={error('name')} isRequired>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onBlur={(e) => setForm((prev) => ({ ...prev, name: capitalizeName(e.target.value) }))}
              isDisabled={saving}
            />
          </FormField>

          <FormField label="E-mail" error={error('email')} isRequired mt={4}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              isDisabled={saving}
            />
          </FormField>

          <AddressFields
            value={address}
            onChange={(patch) => setAddress((prev) => ({ ...prev, ...patch }))}
            error={error}
            isDisabled={saving}
          />

          <Flex gap={4} direction={{ base: 'column', md: 'row' }} mt={4}>
            <FormField label="Modalidade" error={error('modality')} isRequired flex={1}>
              <FormSelect
                value={form.modality}
                onChange={(value) => setForm({ ...form, modality: value as TestimonialVideoModality, courseId: '' })}
                options={MODALITY_OPTIONS}
                hideEmptyOption
                isDisabled={saving}
              />
            </FormField>
            <FormField label="Curso" error={error('course_id')} isRequired flex={2}>
              <SearchableSelect
                placeholder="Selecione o curso..."
                value={form.courseId}
                onChange={(value) => setForm({ ...form, courseId: value })}
                options={courses.map((course) => ({ value: String(course.id), label: course.name }))}
                isLoading={loadingCourses}
                isDisabled={saving}
                entity="curso"
              />
              {coursesLoadFailed && (
                <CoursesLoadError onRetry={() => setCoursesAttempt((n) => n + 1)} isDisabled={saving} />
              )}
            </FormField>
          </Flex>
        </>
      )}
    </FormModal>
  )
}
